<template>
  <div>
    <v-form ref="form" v-model="isFormValid">
      <v-table fixed-header dense>
        <thead>
          <tr>
            <th :colspan="2">Čas události</th>
            <th>Název</th>
            <th>Místo</th>
            <th class="text-center" v-if="schedule.length > 1">Zvýraznit</th>
            <th class="text-center">{{schedule.length > 1 ? 'Smazat' : 'Zvýraznit'}}</th>
          </tr>
        </thead>
        <tbody v-for="(record, idx) in schedule" :key="`row-${record.id}`">
          <tr class="schedule-row">
            <td class="schedule-picker">
              <TimePicker
                class="pt-3"
                :id="`time-from-${record.id}`"
                :neighborId="`time-to-${record.id}`"
                title="Od"
                :clearable="true"
                :rules="[required_rule_compact]"
                v-on:timeChanged="(value) => schedule.find(r => r.id === record.id).timeFrom = value"
                :initialValue="schedule.find(r => r.id === record.id).timeFrom"
                :prependIcon="prependIcon"
                ref="fromPicker">
              </TimePicker>
            </td>
            <td class="schedule-picker">
              <TimePicker
                class="pt-3"
                :id="`time-to-${record.id}`"
                :neighborId="`time-from-${record.id}`"
                title="Do"
                :clearable="true"
                :rules="[required_rule_compact]"
                v-on:timeChanged="(value) => schedule.find(r => r.id === record.id).timeTo = value"
                :initialValue="schedule.find(r => r.id === record.id).timeTo"
                :minTime="schedule.find(r => r.id === record.id).timeFrom"
                :prependIcon="prependIcon"
                ref="toPicker">
              </TimePicker>
            </td>
            <td>
              <v-text-field
                v-bind:key="`${idx}-name`"
                class="pt-3 text-field"
                :rules="[name_rule_compact]" 
                :maxlength="nameMaxLength" 
                color="dark"
                v-model="getEventById(record.id).name"
                :label="`${(getEventById(record.id).name || '').length}/${nameMaxLength} znaků`"
                dense
                variant="underlined"
              >
              </v-text-field>
            </td>
            <td>
              <v-text-field
                v-bind:key="`${idx}-place`"
                class="pt-3 text-field"
                :rules="[place_rule_compact]" 
                :maxlength="placeMaxLength" 
                v-model="getEventById(record.id).place"
                color="dark"
                :label="`${(getEventById(record.id).place || '').length}/${placeMaxLength} znaků`"
                dense
                variant="underlined"
              >
              </v-text-field>
            </td>
            <td>
              <v-checkbox 
                v-model="getEventById(record.id).isImportant"
                :value="getEventById(record.id).isImportant"
                hide-details="true"
                true-value="true"
                false-value="false"
                color="#78BE13"
                class="d-flex justify-center"
              >
              </v-checkbox>
            </td>
            <td v-if="schedule.length > 1" class="py-3 schedule-action text-center">
              <DeleteIcon @click="removeItem(record.id)"></DeleteIcon>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-form>
    <LightButton
      @click="() => schedule = [
        ...schedule, 
        { 
          id: getUUID(),
          timeFrom: undefined,
          timeTo: undefined,
          name: undefined,
          place: undefined
        }
      ]"
    >+ Nová položka</LightButton>
  </div>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import _ from "lodash";
import LightButton from "@/components/buttons/LightButton.vue";
import DeleteIcon from "@/components/buttons/DeleteIcon.vue";
import TimePicker from "@/components/time-picker/TimePickerForDisplays.vue";

const props = defineProps({
  initialValues: Object,
  prependIcon: Boolean,
})

const emit = defineEmits(['eventsChanged'])

const nameMaxLength = 50;
const placeMaxLength = 5;
const isFormValid = ref(false)
const schedule = ref(props.initialValues?.schedule
    ? props.initialValues.schedule.map(item => ({ ...item, id: self.crypto.randomUUID() }))
    : [{
      id: self.crypto.randomUUID(),
      timeFrom: undefined,
      timeTo: undefined,
      name: undefined,
      place: undefined,
      isImportant: false
    }])

const outputObject = computed(()=>{
  return createOutputObject(schedule.value)
})

const initialOutputObject = computed(()=>{
  return createOutputObject(props.initialValues?.schedule);
})

const required_rule_compact = value => !!value || false
const place_rule_compact = value => !!value && value.length <= placeMaxLength || false
const name_rule_compact = value => !!value && value.length <= nameMaxLength || false

function removeItem(id) {
  schedule.value = schedule.value.filter(item => item.id !== id);
}

function createOutputObject(schedule) {
  return schedule?.map(item => _.omit(item, 'id'));
}

function getUUID() {
  return self.crypto.randomUUID();
}

function getEventById(id) {
  return schedule.value.find(event => event.id === id);
}

watch(outputObject, (value) => {
  emit('eventsChanged', { valid: isFormValid.value, pristine: _.isEqual(initialOutputObject, value), value: { schedule: value } })
})

watch(isFormValid, (value) => {
  emit('eventsChanged', { valid: value, pristine: _.isEqual(initialOutputObject, outputObject), value: { schedule: outputObject.value }})
})

</script>


<style>
  .time-from {
    margin-top: 4px;
  }

  .time-to {
    padding-bottom: 16px;
  }

  .schedule-picker {
    min-width: 100px;
    vertical-align: top;
  }

  .text-field {
    min-width: 60px;
  }

  .schedule-action {
    min-width: 100px;
    width: 10%;
  }

  .schedule-single {
    min-width: 150px;
    width: 15%;
  }

  .schedule-table {
    width: 95%;
    border-collapse: collapse;
  }

  .schedule-row {
    border-bottom: 1px solid var(--primary-color);
  }

  table {
    border-collapse: collapse;
  }
</style>